import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Box, Typography, Tooltip, IconButton } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { CloseOutlined } from '@mui/icons-material';
import { CONTAINER_COLOR } from '../constants';
import { uid } from '../utils';
import { ColorInput } from './primitives';

// Shared mutable store — persists across GradientBuilder instances within the session.
export const gradientPresets = [
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
];

export function GradientBuilder({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef();
  const popupRef   = useRef();
  const trackRef   = useRef();

  const parse = (str) => {
    const def = { angle: 135, stops: [{ id: uid(), color: '#667eea', opacity: 1, pos: 0 }, { id: uid(), color: '#764ba2', opacity: 1, pos: 100 }] };
    if (!str || !str.includes('gradient')) return def;
    try {
      const inner = str.match(/linear-gradient\((.+)\)/s)?.[1] || '';
      const parts = inner.split(/,(?![^(]*\))/).map(s => s.trim());
      let angle = 135;
      let stopParts = parts;
      if (parts[0].includes('deg'))   { angle = parseInt(parts[0]); stopParts = parts.slice(1); }
      else if (parts[0].startsWith('to ')) { stopParts = parts.slice(1); }
      const stops = stopParts.map(p => {
        const pos = parseFloat(p.match(/(\d+(?:\.\d+)?)%/)?.[1] ?? '50');
        const rgba = p.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
        if (rgba) {
          const hex = '#' + [rgba[1], rgba[2], rgba[3]].map(x => parseInt(x).toString(16).padStart(2, '0')).join('');
          return { id: uid(), color: hex, opacity: rgba[4] !== undefined ? parseFloat(rgba[4]) : 1, pos };
        }
        const hex = p.match(/#[0-9a-fA-F]{3,6}/)?.[0] || '#000000';
        return { id: uid(), color: hex, opacity: 1, pos };
      }).filter(s => s.color);
      return { angle, stops: stops.length >= 2 ? stops : def.stops };
    } catch { return def; }
  };

  const toCSS = (angle, stops) => {
    const sorted = [...stops].sort((a, b) => a.pos - b.pos);
    return `linear-gradient(${angle}deg, ${sorted.map(s => {
      const r = parseInt(s.color.slice(1,3),16), g = parseInt(s.color.slice(3,5),16), b = parseInt(s.color.slice(5,7),16);
      return `rgba(${r},${g},${b},${s.opacity}) ${s.pos}%`;
    }).join(', ')})`;
  };

  const initial = parse(value);
  const [angle, setAngle] = useState(initial.angle);
  const [stops, setStops] = useState(initial.stops);
  const [selId, setSelId] = useState(initial.stops[0]?.id);

  React.useEffect(() => {
    if (!open) return;
    const p = parse(value);
    setAngle(p.angle);
    setStops(p.stops);
    setSelId(p.stops[0]?.id);
  }, [open]);

  const emit = (a, s) => onChange(toCSS(a, s));

  const updateStop = (id, patch) => {
    const next = stops.map(s => s.id === id ? { ...s, ...patch } : s);
    setStops(next); emit(angle, next);
  };

  const removeStop = (id) => {
    if (stops.length <= 2) return;
    const next = stops.filter(s => s.id !== id);
    setStops(next); setSelId(next[0].id); emit(angle, next);
  };

  const stopsRef = useRef(stops);
  React.useEffect(() => { stopsRef.current = stops; }, [stops]);

  const handleTrackMouseDown = (e) => {
    if (e.target !== trackRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    const pos = Math.round(Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100)));
    const newStop = { id: uid(), color: '#ffffff', opacity: 1, pos };
    const next = [...stopsRef.current, newStop];
    stopsRef.current = next;
    setStops(next); setSelId(newStop.id); emit(angle, next);
  };

  const handleStopMouseDown = (e, id) => {
    e.stopPropagation(); e.preventDefault();
    setSelId(id);
    const rect = trackRef.current.getBoundingClientRect();
    const stop = stopsRef.current.find(s => s.id === id);
    const startX = e.clientX, startPos = stop.pos;

    const onMove = (me) => {
      const delta  = ((me.clientX - startX) / rect.width) * 100;
      const newPos = Math.round(Math.max(0, Math.min(100, startPos + delta)));
      setStops(prev => {
        const next = prev.map(s => s.id === id ? { ...s, pos: newPos } : s);
        stopsRef.current = next;
        emit(angle, next);
        return next;
      });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup',   onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onUp);
  };

  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target) &&
          triggerRef.current && !triggerRef.current.contains(e.target)) {
        const cur = toCSS(angle, stops);
        if (!gradientPresets.includes(cur)) {
          gradientPresets.unshift(cur);
          if (gradientPresets.length > 5) gradientPresets.length = 5;
        }
        setOpen(false);
      }
    };
    setTimeout(() => window.addEventListener('mousedown', handler), 0);
    return () => window.removeEventListener('mousedown', handler);
  }, [open]);

  const [pos, setPos] = useState({ top: 0, left: 0 });
  React.useEffect(() => {
    if (!open || !triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const popH = 460, popW = 260;
    const top  = r.bottom + 6 + popH > window.innerHeight ? r.top - popH - 6 : r.bottom + 6;
    const left = Math.min(r.left, window.innerWidth - popW - 8);
    setPos({ top, left });
  }, [open]);

  const css = toCSS(angle, stops);
  const selStop = stops.find(s => s.id === selId) || stops[0];

  const displayPresets = React.useMemo(() => {
    const cur = toCSS(angle, stops);
    const rest = gradientPresets.filter(g => g !== cur);
    return [cur, ...rest].slice(0, 6);
  }, [css]); // eslint-disable-line

  const closePopup = () => {
    const cur = toCSS(angle, stops);
    if (!gradientPresets.includes(cur)) { gradientPresets.unshift(cur); if (gradientPresets.length > 5) gradientPresets.length = 5; }
    setOpen(false);
  };

  return (
    <>
      <Box ref={triggerRef} onClick={() => setOpen(v => !v)}
        sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', border: `1px solid ${open ? CONTAINER_COLOR : '#e4e9f0'}`, borderRadius: 1.5, p: '4px 8px', background: '#fff', transition: 'border-color 0.15s', '&:hover': { borderColor: CONTAINER_COLOR } }}>
        <Box sx={{ width: 24, height: 24, borderRadius: 1, background: css, flexShrink: 0, border: '1px solid #e4e9f0' }} />
        <Typography sx={{ fontSize: '0.75rem', color: '#5a6474', flex: 1 }}>Edit gradient</Typography>
        <Typography sx={{ fontSize: '0.7rem', color: '#9ea8b3' }}>{angle}°</Typography>
      </Box>

      {open && ReactDOM.createPortal(
        <Box ref={popupRef} onMouseDown={e => e.stopPropagation()}
          sx={{ position: 'fixed', top: pos.top, left: pos.left, width: 260, zIndex: 10000, background: '#fff', borderRadius: 2, boxShadow: '0 8px 32px rgba(0,0,0,0.2)', border: '1px solid #e4e9f0', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid #f0f2f5', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#1a1a2e' }}>Gradient</Typography>
            <IconButton size="small" onClick={closePopup} sx={{ color: '#9ea8b3' }}>
              <CloseOutlined sx={{ fontSize: 15 }} />
            </IconButton>
          </Box>

          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.75, overflowY: 'auto', maxHeight: 420 }}>
            {/* Preview */}
            <Box sx={{ height: 44, borderRadius: 1.5, background: css, border: '1px solid #e8edf2' }} />

            {/* Angle slider */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ fontSize: '0.7rem', color: '#7a8494', fontWeight: 600 }}>Angle</Typography>
                <Typography sx={{ fontSize: '0.7rem', color: CONTAINER_COLOR, fontWeight: 700 }}>{angle}°</Typography>
              </Box>
              <input type="range" min={0} max={360} step={1} value={angle}
                onChange={e => { const v = parseInt(e.target.value); setAngle(v); emit(v, stops); }}
                style={{ width: '100%', cursor: 'pointer', accentColor: CONTAINER_COLOR }} />
            </Box>

            {/* Gradient track */}
            <Box>
              <Typography sx={{ fontSize: '0.7rem', color: '#7a8494', fontWeight: 600, mb: 0.75 }}>
                Color stops
                <Typography component="span" sx={{ fontSize: '0.65rem', color: '#9ea8b3', fontWeight: 400, ml: 0.75 }}>click track to add</Typography>
              </Typography>
              <Box sx={{ position: 'relative', pb: '28px', userSelect: 'none' }}>
                <Box ref={trackRef} onMouseDown={handleTrackMouseDown}
                  sx={{ height: 18, borderRadius: 2, background: css, cursor: 'crosshair', boxShadow: 'inset 0 1px 4px rgba(0,0,0,0.18), 0 1px 0 rgba(255,255,255,0.8)', border: '1px solid rgba(0,0,0,0.08)' }} />
                {stops.map(s => {
                  const r = parseInt(s.color.slice(1,3)||'00',16);
                  const g = parseInt(s.color.slice(3,5)||'00',16);
                  const b = parseInt(s.color.slice(5,7)||'00',16);
                  const isActive = s.id === selId;
                  return (
                    <Box key={s.id} onMouseDown={e => handleStopMouseDown(e, s.id)}
                      sx={{ position: 'absolute', top: '18px', left: `calc(${s.pos}%)`, transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'grab', zIndex: isActive ? 5 : 3, '&:active': { cursor: 'grabbing' } }}>
                      <Box sx={{ width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderBottom: `5px solid ${isActive ? CONTAINER_COLOR : '#c0c8d4'}` }} />
                      <Box sx={{ width: isActive ? 20 : 16, height: isActive ? 20 : 16, borderRadius: '3px', background: `rgba(${r},${g},${b},${s.opacity})`, border: isActive ? `2px solid ${CONTAINER_COLOR}` : '2px solid #c0c8d4', boxShadow: isActive ? `0 0 0 2px ${alpha(CONTAINER_COLOR, 0.25)}, 0 2px 6px rgba(0,0,0,0.2)` : '0 1px 3px rgba(0,0,0,0.15)', transition: 'all 0.12s' }} />
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* Selected stop editor */}
            {selStop && (
              <Box sx={{ background: '#f8f9fb', borderRadius: 1.5, p: 1.25, border: '1px solid #e8edf2' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#3c4555' }}>Stop at {selStop.pos}%</Typography>
                  <Box onClick={() => removeStop(selStop.id)} sx={{ fontSize: '0.65rem', fontWeight: 700, cursor: stops.length > 2 ? 'pointer' : 'default', color: stops.length > 2 ? '#d32f2f' : '#c0c8d4' }}>Remove</Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  <ColorInput value={selStop.color} onChange={v => updateStop(selStop.id, { color: v })} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '0.68rem', color: '#7a8494', flexShrink: 0, width: 50 }}>Opacity</Typography>
                    <input type="range" min={0} max={1} step={0.01} value={selStop.opacity}
                      onChange={e => updateStop(selStop.id, { opacity: parseFloat(e.target.value) })}
                      style={{ flex: 1, cursor: 'pointer', accentColor: CONTAINER_COLOR }} />
                    <Typography sx={{ fontSize: '0.68rem', color: '#5a6474', width: 28, textAlign: 'right', flexShrink: 0 }}>
                      {Math.round(selStop.opacity * 100)}%
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {/* Presets */}
            <Box>
              <Typography sx={{ fontSize: '0.7rem', color: '#7a8494', fontWeight: 600, mb: 0.75 }}>Presets</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {displayPresets.map((g, i) => (
                  <Tooltip key={g} title={i === 0 ? 'Current gradient' : ''} placement="top">
                    <Box onClick={() => { const p = parse(g); setAngle(p.angle); setStops(p.stops); setSelId(p.stops[0]?.id); onChange(g); }}
                      sx={{ width: 28, height: 28, borderRadius: 1, cursor: 'pointer', background: g, flexShrink: 0, position: 'relative', border: css === g ? `2px solid ${CONTAINER_COLOR}` : i === 0 ? `2px solid ${alpha(CONTAINER_COLOR, 0.4)}` : '2px solid transparent', '&:hover': { transform: 'scale(1.1)' }, transition: 'transform 0.1s' }}>
                      {i === 0 && <Box sx={{ position: 'absolute', top: -4, right: -4, width: 8, height: 8, borderRadius: '50%', background: CONTAINER_COLOR, border: '1px solid #fff' }} />}
                    </Box>
                  </Tooltip>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>,
        document.body
      )}
    </>
  );
}